import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { fetchEntryPoints } from '@/services/campaign';
import { EntryLinkDialog } from '@/components/dialog/entry-link-dialog';
import { EntryLinkListDialog } from '@/components/dialog/entry-link-list-dialog';
import { QrCustomizeDialog } from '@/components/dialog/qr-customize-Dialog';
import { LinkIcon } from 'lucide-react';
export const CampaignEntryPoints = ({ campaign }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: entryPoints = [] } = useQuery({
        queryKey: ['campaign-entry-points', campaign.publicId],
        queryFn: () => fetchEntryPoints(campaign.publicId),
        enabled: !!campaign.publicId,
    });
    const handleSuccess = React.useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['campaign-entry-points', campaign.publicId] });
    }, [campaign.publicId, queryClient]);
    const defaultEp = entryPoints.find((entryPoint) => entryPoint.isDefault);
    const handleCopy = (entryPoint) => navigator.clipboard
        .writeText(entryPoint.publicUrl)
        .then(() => toast.success(t('copied_clipboard')))
        .catch(() => toast.error(t('copy_failed', { ns: 'error' })));
    return (<div>
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <h3 className="text-lg font-semibold">{t('entry_points.title', { ns: 'glossary' })}</h3>
                    <p className="text-xs text-gray-400">{t('entry_points.help', { ns: 'glossary' })}</p>
                </div>
                <div className="flex flex-col items-center gap-2 md:flex-row">
                    <EntryLinkListDialog entryPoints={entryPoints} onCopy={handleCopy} onSuccess={handleSuccess}/>
                    <EntryLinkDialog campaign={campaign} onSuccess={handleSuccess}/>
                </div>
            </div>

            {defaultEp && (<div className="rounded-lg border border-gray-800 p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold">
                                    {t('entry_points.default.title', { ns: 'glossary' })}
                                </p>
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-700 text-gray-400">
                                    {t('system')}
                                </span>
                            </div>

                            <p className="text-xs text-gray-400 truncate">
                                {defaultEp?.name ??
                defaultEp?.context?.label ??
                t('entry_points.default.context', { ns: 'glossary' })}
                            </p>
                        </div>
                        <div className="flex flex-col items-center gap-2 md:flex-row">
                            <QrCustomizeDialog entryPoint={defaultEp}/>
                            <Button variant="outline" size="sm" onClick={() => handleCopy(defaultEp)}>
                                <LinkIcon className="w-4 h-4"/>
                                {t('copy_link')}
                            </Button>
                        </div>
                    </div>

                    <p className="mt-2 text-xs text-gray-500">{t('entry_points.default.help', { ns: 'glossary' })}</p>
                </div>)}
        </div>);
};
CampaignEntryPoints.displayName = 'CampaignEntryPoints';
